import { isEqual } from 'lodash';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColDef, GridPinnedColumnFields } from '@mui/x-data-grid-pro';
import { ColumnDensity, SORT_ORDER } from '@common/types';

export type PersistedOrganizationColumn = Pick<
  GridColDef,
  | 'field'
  | 'headerName'
  | 'width'
  | 'minWidth'
  | 'flex'
  | 'sortable'
  | 'filterable'
  | 'disableColumnMenu'
  | 'align'
  | 'headerAlign'
>;

export const initialOrganizationColumns: PersistedOrganizationColumn[] = [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 220,
    flex: 1,
    sortable: true,
    filterable: false,
  },
  {
    field: 'user_count',
    headerName: 'Users',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
  },
  {
    field: 'partner_count',
    headerName: 'Partners',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
  },
  {
    field: 'admin_count',
    headerName: 'Admins',
    width: 120,
    minWidth: 120,
    sortable: true,
    filterable: false,
  },
];

export const initialOrganizationPinnedColumns: GridPinnedColumnFields = {
  left: [],
  right: [],
};

export type ColumnNames =
  'name' | 'user_count' | 'partner_count' | 'admin_count';

export interface IOrganizationTableFilters {
  search: string;
  sort_by: ColumnNames;
  sort_order: SORT_ORDER;
  page: number;
  limit: number;
}

const getDefaultFilters = (): IOrganizationTableFilters => ({
  search: '',
  sort_order: 'asc',
  sort_by: 'admin_count',
  page: 1,
  limit: 10,
});

type TKeyOfFilters = keyof IOrganizationTableFilters;

const getAppliedFiltersCount = (
  initialFiltersList: IOrganizationTableFilters,
  appliedFilters: IOrganizationTableFilters,
): number => {
  const filtersKeyList: TKeyOfFilters[] = Object.keys(appliedFilters).filter(
    key =>
      key !== 'page' &&
      key !== 'limit' &&
      key !== 'search' &&
      key !== 'sort_order' &&
      key !== 'sort_by',
  ) as TKeyOfFilters[];

  return filtersKeyList.reduce(
    (count, key) =>
      isEqual(initialFiltersList[key], appliedFilters[key]) ? count : count + 1,
    0,
  );
};

type TOrganizationTableStore = {
  filters: IOrganizationTableFilters;
  setFilters: (newFilters: Partial<IOrganizationTableFilters>) => void;
  clearStore: () => void;
  appliedFiltersCount: number;

  columns: PersistedOrganizationColumn[];
  setColumns: (columns: PersistedOrganizationColumn[]) => void;
  pinnedColumns: GridPinnedColumnFields;
  setPinnedColumns: (pinnedColumns: GridPinnedColumnFields) => void;
  hiddenColumns: string[];
  setHiddenColumns: (hiddenColumns: string[]) => void;
  density: ColumnDensity;
  setDensity: (density: ColumnDensity) => void;
  resetView: () => void;
};

export const useOrganizationTableStore = create<TOrganizationTableStore>()(
  persist(
    set => ({
      filters: getDefaultFilters(),
      setFilters: newFilters =>
        set(state => {
          const updatedFilters = { ...state.filters, ...newFilters };
          return {
            filters: updatedFilters,
            appliedFiltersCount: getAppliedFiltersCount(
              getDefaultFilters(),
              updatedFilters,
            ),
          };
        }),
      clearStore: () =>
        set(() => ({
          filters: getDefaultFilters(),
          appliedFiltersCount: 0,
        })),
      appliedFiltersCount: 0,

      columns: initialOrganizationColumns,
      setColumns: columns => set({ columns }),
      pinnedColumns: initialOrganizationPinnedColumns,
      setPinnedColumns: pinnedColumns => set({ pinnedColumns }),
      hiddenColumns: [],
      setHiddenColumns: hiddenColumns => set({ hiddenColumns }),
      density: 'comfortable',
      setDensity: density => set({ density }),
      resetView: () =>
        set({
          columns: initialOrganizationColumns,
          pinnedColumns: initialOrganizationPinnedColumns,
          hiddenColumns: [],
          density: 'comfortable',
        }),
    }),
    {
      name: 'organization-table-store',
      partialize: state => ({
        columns: state.columns,
        pinnedColumns: state.pinnedColumns,
        hiddenColumns: state.hiddenColumns,
        density: state.density,
        sort_by: state.filters.sort_by,
        sort_order: state.filters.sort_order,
      }),
      merge: (persistedState, currentState) => {
        const persisted = (persistedState ?? {}) as Partial<
          TOrganizationTableStore &
            Pick<IOrganizationTableFilters, 'sort_by' | 'sort_order'>
        >;

        const mergedFilters: IOrganizationTableFilters = {
          ...currentState.filters,
          ...(persisted.sort_by && { sort_by: persisted.sort_by }),
          ...(persisted.sort_order && { sort_order: persisted.sort_order }),
        };

        return {
          ...currentState,
          ...persisted,
          filters: mergedFilters,
        };
      },
    },
  ),
);
